/**
 * Save 직전, 후보 슬라이스에 대한 규격 외 데이터 카운트를 보여주는 모달.
 *
 * 레이아웃:
 * - 상단: 위반 총계 (4종 합) + 간단 요약
 * - 본문: Unknown kinds / Unknown domains / Unknown relations / Shape violations 섹션
 *   각 섹션은 key/count/sample 을 나열 (sample 은 최대 3건까지)
 * - 하단: 취소 / "이대로 저장" 버튼. 규격 외 데이터가 있으면 경고 문구 (저장 후 knowledge_upsert 거부됨).
 *
 * **규격화된 그래프 RAG**: 슬라이스 밖 데이터는 항상 400. enforcement "승격" 개념은 없으며,
 * 모달은 "규격 외 발견 수" 를 단순 정보성으로 표시한다.
 */
import type { OntologyViolations } from "../../api/admin-client.ts"

interface PreviewImpactModalProps {
	readonly violations: OntologyViolations
	readonly loading?: boolean
	readonly saving?: boolean
	readonly onCancel: () => void
	readonly onConfirm: () => void
}

function totalCount(v: OntologyViolations): number {
	let sum = 0
	for (const g of v.unknownKinds) sum += g.count
	for (const g of v.unknownDomains) sum += g.count
	for (const g of v.unknownRelations) sum += g.count
	for (const g of v.shapeViolations) sum += g.count
	return sum
}

export function PreviewImpactModal(props: PreviewImpactModalProps) {
	const total = totalCount(props.violations)

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onCancel()}
		>
			<div
				className="modal preview-impact-modal"
				onMouseDown={(e) => e.stopPropagation()}
				style={{ maxWidth: 720 }}
			>
				<div className="modal-head">
					<h2>변경 영향 미리보기</h2>
					<p>현재 draft 를 적용하면 아래 항목들이 슬라이스 밖으로 감지됩니다.</p>
				</div>

				<div className="modal-body">
					{props.loading ? (
						<p className="muted">위반 카운트를 계산하는 중…</p>
					) : total === 0 ? (
						<div className="alert alert-info">
							슬라이스 밖 항목이 감지되지 않았습니다. 안전하게 저장할 수 있습니다.
						</div>
					) : (
						<>
							<div className="alert alert-warning" style={{ marginBottom: 14 }}>
								슬라이스에 정의되지 않은 항목이 있습니다. 저장 후 이런 노드는{" "}
								<code className="inline">knowledge_upsert</code> 단계에서 <strong>거부</strong>됩니다. 먼저
								슬라이스에 해당 kind / domain / relation / shape 를 흡수하거나, 데이터 측에서 정정하세요.
							</div>

							<div className="preview-impact-summary">
								<PreviewStat
									label="미정의 kind"
									count={props.violations.unknownKinds.reduce((a, g) => a + g.count, 0)}
								/>
								<PreviewStat
									label="미정의 domain"
									count={props.violations.unknownDomains.reduce((a, g) => a + g.count, 0)}
								/>
								<PreviewStat
									label="미정의 relation"
									count={props.violations.unknownRelations.reduce((a, g) => a + g.count, 0)}
								/>
								<PreviewStat
									label="Shape 위반"
									count={props.violations.shapeViolations.reduce((a, g) => a + g.count, 0)}
								/>
							</div>

							<ViolationGroupList
								title="미정의 kind"
								groups={props.violations.unknownKinds.map((g) => ({
									key: g.key,
									count: g.count,
									sample: g.sample
								}))}
							/>
							<ViolationGroupList
								title="미정의 domain"
								groups={props.violations.unknownDomains.map((g) => ({
									key: g.key,
									count: g.count,
									sample: g.sample
								}))}
							/>
							<ViolationGroupList
								title="미정의 relation"
								groups={props.violations.unknownRelations.map((g) => ({
									key: g.key,
									count: g.count,
									sample: g.sample
								}))}
							/>
							<ShapeViolationList groups={props.violations.shapeViolations} />
						</>
					)}
				</div>

				<div className="modal-foot">
					<button type="button" className="btn btn-ghost" onClick={props.onCancel} disabled={props.saving}>
						취소
					</button>
					<button type="button" className="btn btn-primary" onClick={props.onConfirm} disabled={props.saving}>
						{props.saving ? "저장 중…" : "이대로 저장"}
					</button>
				</div>
			</div>
		</dialog>
	)
}

function PreviewStat(props: { readonly label: string; readonly count: number }) {
	return (
		<div className={`preview-stat${props.count > 0 ? " is-warn" : ""}`}>
			<span className="preview-stat-count">{props.count}</span>
			<span className="preview-stat-label">{props.label}</span>
		</div>
	)
}

interface SimpleGroup {
	readonly key: string
	readonly count: number
	readonly sample: ReadonlyArray<string>
}

function ViolationGroupList(props: { readonly title: string; readonly groups: ReadonlyArray<SimpleGroup> }) {
	if (props.groups.length === 0) return null
	return (
		<div className="violation-group">
			<h4>{props.title}</h4>
			<ul>
				{props.groups.map((g) => (
					<li key={g.key}>
						<code className="inline">{g.key}</code>
						<span className="badge">{g.count}</span>
						{g.sample.length > 0 && (
							<span className="muted sample">
								{g.sample.slice(0, 3).join(", ")}
								{g.sample.length > 3 ? ` +${g.sample.length - 3}` : ""}
							</span>
						)}
					</li>
				))}
			</ul>
		</div>
	)
}

function ShapeViolationList(props: {
	readonly groups: ReadonlyArray<{
		readonly sourceKind: string
		readonly relation: string
		readonly targetKind: string
		readonly count: number
		readonly sample: ReadonlyArray<{ readonly fromId: string; readonly toId: string }>
	}>
}) {
	if (props.groups.length === 0) return null
	return (
		<div className="violation-group">
			<h4>Shape 위반</h4>
			<ul>
				{props.groups.map((g) => {
					const key = `${g.sourceKind}|${g.relation}|${g.targetKind}`
					return (
						<li key={key}>
							<code className="inline">
								{g.sourceKind} —[{g.relation}]→ {g.targetKind}
							</code>
							<span className="badge">{g.count}</span>
							{g.sample.length > 0 && (
								<span className="muted sample">
									{g.sample
										.slice(0, 2)
										.map((s) => `${s.fromId}→${s.toId}`)
										.join(", ")}
									{g.sample.length > 2 ? ` +${g.sample.length - 2}` : ""}
								</span>
							)}
						</li>
					)
				})}
			</ul>
		</div>
	)
}
